/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetTradesRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { AmmUserActivitySchema, MarketTradeSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── live re-rank · shared comparators ─────────────────────────────────────

function _toBigIntOrZero(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || !/^-?\d+$/.test(t)) return 0n;
    try {
      return BigInt(t);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function _cmpD6(a: unknown, b: unknown): number {
  const av = _toBigIntOrZero(a);
  const bv = _toBigIntOrZero(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}

function _cmpNum(a: unknown, b: unknown): number {
  const av = typeof a === 'number' ? a : Number(a);
  const bv = typeof b === 'number' ? b : Number(b);
  const an = Number.isFinite(av) ? av : 0;
  const bn = Number.isFinite(bv) ? bv : 0;
  return an < bn ? -1 : an > bn ? 1 : 0;
}

function _cmpStr(a: unknown, b: unknown): number {
  const av = a == null ? '' : String(a);
  const bv = b == null ? '' : String(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}


// ─── useGetTrades ────────────────────────────────────────────────────────────

export interface useGetTradesArgs { readonly market?: string; readonly markets?: readonly string[]; readonly user?: string; readonly users?: string; readonly outcomeIndex?: string; readonly type?: string; readonly from?: string; readonly to?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly signatures?: string; readonly mints?: readonly string[]; readonly marketMintRoutes?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('eventId' | 'signature' | 'market' | 'outcomeIndex' | 'type' | 'side' | 'liquidityModel' | 'usdcAmount' | 'tokensAmount' | 'totalCost' | 'platformFee' | 'lpFee' | 'prizeFee' | 'priceBefore' | 'priceAfter' | 'timestamp' | 'thesis' | 'trader')[]; }


async function _useGetTradesQueryFn(
  args: useGetTradesArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetTradesRow>> {
  const url = '/v1/trades';
  const params = new URLSearchParams();

  if (args.market !== undefined) params.set('market', String(args.market));
  if (args.markets !== undefined) params.set('markets', String(args.markets));
  if (args.user !== undefined) params.set('user', String(args.user));
  if (args.users !== undefined) params.set('users', String(args.users));
  if (args.outcomeIndex !== undefined) params.set('outcomeIndex', String(args.outcomeIndex));
  if (args.type !== undefined) params.set('type', String(args.type));
  if (args.from !== undefined) params.set('from', String(args.from));
  if (args.to !== undefined) params.set('to', String(args.to));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  if (args.signatures !== undefined) params.set('signatures', String(args.signatures));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetTrades',
    routeTemplate: '/v1/trades',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetTradesRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sortBy ?? null, order: args.sortOrder ?? null },
  };
}


const _useGetTradesFactory = Object.assign(
  (args: useGetTradesArgs) => ({
    queryKey: ['trades', 'list', { market: args.market ?? null, markets: args.markets ?? null, user: args.user ?? null, users: args.users ?? null, outcomeIndex: args.outcomeIndex ?? null, type: args.type ?? null, from: args.from ?? null, to: args.to ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null, signatures: args.signatures ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'eventId'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetTradesQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetTrades',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['trades', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetTradesArgs, PagedData<GetTradesRow>>;


export const useGetTradesSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/trades',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetTradesFactory },
  parseInput: (raw: unknown): useGetTradesArgs => raw as useGetTradesArgs,
  parseResponse: (raw: unknown): PagedData<GetTradesRow> => raw as PagedData<GetTradesRow>,
  specName: 'useGetTrades' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['trades', 'entity', id] as readonly unknown[],
  entityGcTimeMs: 300000,
  rerankSiblingFeeds: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetTradesRow>>;
export const useGetTradesPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetTradesArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetTradesQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;


// ─── useGetTrades · live re-rank ──────────────────────────────────────────────

/** Sort-key value → { row field, comparator } — from `sort.keys`. */
const _useGetTradesSortKeys: Record<
  string,
  { readonly field: string; readonly cmp: (a: unknown, b: unknown) => number }
> = {
  'timestamp': { field: 'timestamp', cmp: _cmpStr },
};

/**
 * Pure client-side re-sort of the loaded `GetTradesRow` rows by the active
 * sort key. `by` indexes `_useGetTradesSortKeys`; `order` is `asc`/`desc`
 * (null ⇒ `desc`). Ties break on `signature` ascending. Returns
 * the input array unchanged when `by` is null / unknown.
 */
function resortGetTradesRows(
  rows: readonly GetTradesRow[],
  by: string | null,
  order: string | null,
): readonly GetTradesRow[] {
  if (by === null) return rows;
  const _k = _useGetTradesSortKeys[by];
  if (_k === undefined) return rows;
  const _dir = order === 'asc' ? 1 : -1;
  const _out = rows.slice();
  _out.sort((a, b) => {
    const _c = _k.cmp(
      (a as unknown as Record<string, unknown>)[_k.field],
      (b as unknown as Record<string, unknown>)[_k.field],
    );
    if (_c !== 0) return _c * _dir;
    return _cmpStr(
      (a as unknown as Record<string, unknown>)['signature'],
      (b as unknown as Record<string, unknown>)['signature'],
    );
  });
  return _out;
}

/**
 * Envelope wrapper — re-ranks `env.data` by `env._sort` and preserves
 * `_sort` on the result. The realtime reducer routes every row-patch
 * return through this so a live list never drifts out of sort order.
 */
function _useGetTradesResort(
  env: PagedData<GetTradesRow>,
): PagedData<GetTradesRow> {
  const _by = env._sort?.by ?? null;
  if (_by === null) return env;
  const _resorted = resortGetTradesRows(env.data, _by, env._sort?.order ?? null);
  return _resorted === env.data ? env : { ...env, data: _resorted };
}


function _useGetTradesSubjectArgValues(value: unknown): readonly string[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const values = new Set<string>();
  for (const rawValue of rawValues) {
    if (typeof rawValue !== 'string') continue;
    const normalized = rawValue.trim();
    if (normalized !== '') values.add(normalized);
  }
  return [...values];
}

function _useGetTradesHasSubjectArg(value: unknown): boolean {
  return _useGetTradesSubjectArgValues(value).length > 0;
}

function _useGetTradesPerEntitySubjects(args: useGetTradesArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [...(_useGetTradesHasSubjectArg(args.market) ? [`morfi.market.${args.market}.user.*.trade.v0`] : []), ...(!_useGetTradesHasSubjectArg(args.market) && _useGetTradesHasSubjectArg(args.markets) ? [..._useGetTradesSubjectArgValues(args.markets).map((_subjectArg0) => `morfi.market.${_subjectArg0}.user.*.trade.v0`)] : []), ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetTradesRowSubjectTemplates(args: useGetTradesArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [...(_useGetTradesHasSubjectArg(args.market) ? [`morfi.market.${args.market}.user.*.trade.v0`] : []), ...(!_useGetTradesHasSubjectArg(args.market) && _useGetTradesHasSubjectArg(args.markets) ? [..._useGetTradesSubjectArgValues(args.markets).map((_subjectArg0) => `morfi.market.${_subjectArg0}.user.*.trade.v0`)] : []), ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetTradesGatedSubjects(args: useGetTradesArgs): readonly string[] {
  // Subjects fanned out from an ARRAY hook arg. The runtime holds each one
  // only while the entity it names is visible in some row on the page.
  const _subs: readonly string[] = [...(!_useGetTradesHasSubjectArg(args.market) && _useGetTradesHasSubjectArg(args.markets) ? [..._useGetTradesSubjectArgValues(args.markets).map((_subjectArg0) => `morfi.market.${_subjectArg0}.user.*.trade.v0`)] : []), ...(args.mints ?? []).map((_wp) => `morfi.amm.${_wp}.user.*.activity.v0`)];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetTradesApplyEvent(
  prev: PagedData<GetTradesRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetTradesArgs,
): PagedData<GetTradesRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // prepend · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _newRow = HELPERS.buildTradeRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetTradesRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['eventId'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['eventId'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return _useGetTradesResort({ ...prev, data: _patched });
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetTradesResort({ ...prev, data: _capped });
    }
  }

  // prepend · morfi.market.${m}.user.*.trade.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      const _newRow = HELPERS.buildTradeRowFromEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetTradesRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['eventId'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['eventId'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return _useGetTradesResort({ ...prev, data: _patched });
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetTradesResort({ ...prev, data: _capped });
    }
  }

  // prepend · morfi.amm.${mint}.user.*.activity.v0
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      const _newRow = HELPERS.buildTradeRowFromAmmEvent(raw, subject, args as unknown as Record<string, unknown>) as unknown as GetTradesRow;
      const _rowId = (_newRow as unknown as Record<string, unknown>)['eventId'];
      // Helpers signal "this event isn't for this cache slice" by
      // returning a row whose primary key is the empty string — codegen
      // bails here so we don't prepend/replace into the wrong list.
      if (typeof _rowId !== 'string' || _rowId.length === 0) return prev;
      // Prepend-if-new, replace-if-present. A later event on the SAME
      // row id (e.g. a GiftClaimed for a previously-CREATED gift, or a
      // thesis EDIT after POSTED) rebuilds the row via the helper and
      // patches it IN PLACE so the cached row reflects post-state
      // without a refetch. Order is preserved (no re-prepend, no
      // duplicate).
      for (let _i = 0; _i < prevRows.length; _i += 1) {
        if ((prevRows[_i] as unknown as Record<string, unknown>)['eventId'] === _rowId) {
          const _patched = prevRows.slice();
          _patched[_i] = _newRow;
          if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
          return _useGetTradesResort({ ...prev, data: _patched });
        }
      }
      // Pagination may have deliberately loaded more than the default live
      // cap. Preserve that depth and evict only one oldest row per prepend;
      // otherwise the first realtime event truncates a complete chart/feed.
      const _retainedRows = Math.max(200, prevRows.length);
      const _capped =
        prevRows.length + 1 > _retainedRows
          ? [_newRow, ...prevRows.slice(0, _retainedRows - 1)]
          : [_newRow, ...prevRows];
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetTradesResort({ ...prev, data: _capped });
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetTradesRealtime = {
  domain: 'trades' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0'] as readonly string[],
  rowKey: 'eventId' as const,
  perEntitySubjects: (args: useGetTradesArgs) => _useGetTradesPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetTradesArgs) => _useGetTradesRowSubjectTemplates(args),
  gatedSubjects: (args: useGetTradesArgs) => _useGetTradesGatedSubjects(args),
  rerank: (prev: PagedData<GetTradesRow>): PagedData<GetTradesRow> => _useGetTradesResort(prev),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetTradesArgs) =>
    (prev: PagedData<GetTradesRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetTradesRow> | undefined =>
      _useGetTradesApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetTradesRow>, unknown>;
